import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Input, InputProps } from '@/components/ui-kit/input';

export interface HobbiesInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'max' | 'disabled' | 'placeholder'
> {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  /** The legacy chip input accepts at most this many entries. */
  max: number;
  disabled?: boolean;
}

/** Chip input for hobbies: Enter or a comma commits the typed text; input is locked once `max` chips exist. */
export const HobbiesInput = ({
  value,
  onChange,
  placeholder,
  max,
  disabled,
  ...inputProps
}: HobbiesInputProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const isFull = value.length >= max;

  /** Commits every non-empty entry, up to the limit. */
  const commit = (entries: string[]) => {
    const next = [...value];
    entries
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        if (next.length < max) next.push(entry);
      });
    if (next.length !== value.length) onChange(next);
  };

  const handleChange = (text: string) => {
    // A comma (typed or pasted) ends an entry, as in the legacy chip input.
    if (!text.includes(',')) {
      setDraft(text);
      return;
    }
    const parts = text.split(',');
    commit(parts.slice(0, -1));
    setDraft(parts[parts.length - 1]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commit([draft]);
    setDraft('');
  };

  const remove = (index: number) => onChange(value.filter((_, position) => position !== index));

  return (
    <div className="flex flex-col gap-2">
      <Input
        {...inputProps}
        value={draft}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={isFull ? '' : placeholder}
        disabled={disabled || isFull}
      />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((hobby, index) => (
            <Badge key={`${hobby}-${index}`} variant="outline" className="gap-1" title={hobby}>
              <span className="max-w-40 truncate">{hobby}</span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`${t('REMOVE')} ${hobby}`}
                disabled={disabled}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
